import { callbackBusinessDayNames } from '@jlg-website/shared';
import type { CallbackSettingsInput } from '@jlg-website/shared';
import { DATE_FORMAT, dateTime } from './date-time';

type BusinessInterval = { start: string; end: string };

type OfficeDayQuery = {
  date: string;
  settings: CallbackSettingsInput;
  visitorTimeZone: string;
};

/** First and last slot of a day, as instants. */
export type OfficeDayBounds = {
  opensAt: number;
  lastSlotStartsAt: number;
};

const toMinuteOfDay = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Yields lazily so a caller that only needs to know whether *some* slot is free stops at the
 * first one instead of building the whole day.
 */
function* eachOfficeDayStart(
  officeDate: string,
  settings: CallbackSettingsInput
): Generator<string> {
  const weekday = callbackBusinessDayNames[dateTime.tz(officeDate, settings.tz).day()];

  for (const interval of settings.business_days[weekday] as BusinessInterval[]) {
    const opening = dateTime.tz(`${officeDate} ${interval.start}`, settings.tz);
    const span = toMinuteOfDay(interval.end) - toMinuteOfDay(interval.start);

    for (
      let offset = 0;
      offset + settings.slot_duration <= span;
      offset += settings.slot_duration
    ) {
      yield opening.add(offset, 'minute').toISOString();
    }
  }
}

/**
 * Office days whose slots can land on `visitorDate`. The office and the visitor may sit in
 * different zones, so a slot opened on the office's previous or next day can still read as this
 * date to the visitor. The visitor's own day comes first: it holds the match almost every time,
 * which is what lets the lazy callers stop early.
 */
const officeDatesAround = (visitorDate: string) =>
  [0, -1, 1].map((offset) => dateTime(visitorDate).add(offset, 'day').format(DATE_FORMAT));

/** Every slot start that reads as `date` on the visitor's clock, in office order. */
export function* eachSlotStart({
  date,
  settings,
  visitorTimeZone,
}: OfficeDayQuery): Generator<string> {
  for (const officeDate of officeDatesAround(date)) {
    for (const startsAt of eachOfficeDayStart(officeDate, settings)) {
      if (dateTime(startsAt).tz(visitorTimeZone).format(DATE_FORMAT) === date) yield startsAt;
    }
  }
}

/**
 * The edges of the visitor's day, or `null` when the office takes no callbacks on it. Walks the
 * starts once and keeps only the extremes — the caller that just needs the day's shape never pays
 * for the slot list itself.
 */
export function officeDayBounds(query: OfficeDayQuery): OfficeDayBounds | null {
  let opensAt = Infinity;
  let lastSlotStartsAt = -Infinity;

  for (const startsAt of eachSlotStart(query)) {
    const instant = Date.parse(startsAt);

    if (instant < opensAt) opensAt = instant;
    if (instant > lastSlotStartsAt) lastSlotStartsAt = instant;
  }

  return lastSlotStartsAt === -Infinity ? null : { opensAt, lastSlotStartsAt };
}
